import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import VendorFormModal from '../components/VendorFormModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import './VendorList.css';
import { toast } from '../components/Toast';

function VendorList() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedVendors = React.useMemo(() => {
    let sortable = [...filteredVendors];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'createdAt') {
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
        }

        if (aVal === undefined || aVal === null) aVal = '';
        if (bVal === undefined || bVal === null) bVal = '';

        if (typeof aVal === 'string') {
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        } else {
          return sortConfig.direction === 'asc'
            ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0)
            : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
        }
      });
    }
    return sortable;
  }, [filteredVendors, sortConfig]);

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    filterVendors();
  }, [vendors, searchTerm]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/vendors');
      const vendorData = response.data.data?.content || response.data.content || response.data;
      setVendors(vendorData);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const filterVendors = () => {
    let filtered = [...vendors];

    if (searchTerm) {
      filtered = filtered.filter(vendor =>
        vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.phone.includes(searchTerm) ||
        (vendor.email && vendor.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (vendor.vendorType && vendor.vendorType.toLowerCase().includes(searchTerm.toLowerCase())) ||
        vendor.id.toString().includes(searchTerm)
      );
    }

    setFilteredVendors(filtered);
    setCurrentPage(0);
  };

  const handleCreate = () => {
    setSelectedVendor(null);
    setShowModal(true);
  };

  const handleEdit = (vendor) => {
    setSelectedVendor(vendor);
    setShowModal(true);
  };

  const handleView = (vendor) => {
    navigate(`/vendors/${vendor.id}`);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedVendor(null);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setSelectedVendor(null);
    fetchVendors();
    showSuccessMessage('Vendor saved successfully');
  };

  const handleDelete = (vendor) => {
    setVendorToDelete(vendor);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!vendorToDelete) return;
    try {
      await axios.delete(`/vendors/${vendorToDelete.id}`);
      setShowConfirmModal(false);
      setVendorToDelete(null);
      fetchVendors();
      showSuccessMessage('Vendor deleted successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete vendor');
      setShowConfirmModal(false);
      setVendorToDelete(null);
    }
  };

  const showSuccessMessage = (message) => {
    toast.success(message);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="vendor-list-container">
      <div className="page-header">
        <h1>Vendor Management</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          Create Vendor
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by ID, name, phone, email, or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="btn btn-secondary btn-sm" onClick={clearSearch}>
              Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading vendors...</div>
      ) : (
        <div className="table-container">
          <table className="vendor-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Name {getSortIcon('name')}</th>
                <th onClick={() => requestSort('phone')} style={{ cursor: 'pointer', userSelect: 'none' }}>Phone {getSortIcon('phone')}</th>
                <th onClick={() => requestSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>Email {getSortIcon('email')}</th>
                <th onClick={() => requestSort('vendorType')} style={{ cursor: 'pointer', userSelect: 'none' }}>Type {getSortIcon('vendorType')}</th>
                <th onClick={() => requestSort('address')} style={{ cursor: 'pointer', userSelect: 'none' }}>Address {getSortIcon('address')}</th>
                <th onClick={() => requestSort('createdAt')} style={{ cursor: 'pointer', userSelect: 'none' }}>Created At {getSortIcon('createdAt')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedVendors.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">No vendors found</td>
                </tr>
              ) : (
                sortedVendors.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage).map((vendor) => (
                  <tr key={vendor.id}>
                    <td data-label="Name"><strong>{vendor.name}</strong></td>
                    <td data-label="Phone">{vendor.phone}</td>
                    <td data-label="Email">{vendor.email || '-'}</td>
                    <td data-label="Type">{vendor.vendorType || '-'}</td>
                    <td data-label="Address">{vendor.address || '-'}</td>
                    <td data-label="Created At">{new Date(vendor.createdAt).toLocaleDateString()}</td>
                    <td data-label="Actions">
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleView(vendor)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(vendor)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(vendor)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {Math.ceil(filteredVendors.length / itemsPerPage) > 1 && (
            <div className="pagination" style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
              <button
                className="btn btn-sm"
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage + 1} of {Math.ceil(filteredVendors.length / itemsPerPage)} ({filteredVendors.length} total)
              </span>
              <button
                className="btn btn-sm"
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredVendors.length / itemsPerPage) - 1, prev + 1))}
                disabled={currentPage >= Math.ceil(filteredVendors.length / itemsPerPage) - 1}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <VendorFormModal
          vendor={selectedVendor}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      <DeleteConfirmModal
        isOpen={showConfirmModal}
        title="Delete Vendor"
        message={<>Are you sure you want to delete vendor <strong>{vendorToDelete?.name}</strong>?</>}
        onCancel={() => { setShowConfirmModal(false); setVendorToDelete(null); }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

export default VendorList;
